import React, { useCallback } from 'react';
import { useGiftWrapContext } from './context/GiftWrapContext.jsx';
import { UserInputProvider, useUserInput } from './context/UserInputContext.tsx';
import { GiftWrapModal } from './components/GiftWrapModal/index';
import { PageProvider, usePage } from './context/PageContext.jsx';
import { PackagingProvider, usePackaging } from './packaging/PackagingContext';
import { isPackagingFlowEnabled } from './packaging/flag';
import { giftWrapEventListener } from './services/giftWrapEventListener';

/**
 * Inner component that has access to all contexts.
 * @returns {import('react').ReactElement}
 */
function GiftWrapModals() {
  const { giftWrapModalOpen, setGiftWrapModalOpen, resetGiftWrapState, addToCartLoading } = useGiftWrapContext();
  const userInput = useUserInput();
  const { resetPageContext } = usePage();
  const packaging = usePackaging();
  const handleCloseGiftWrapModal = useCallback(() => {
    // Reset all contexts when modal closes.
    resetGiftWrapState();
    userInput.clearAll();
    resetPageContext();
    setGiftWrapModalOpen(false);
    // Closing the packaging journey declines it: drop the pending add so a later cart-drawer wrap starts clean.
    if (isPackagingFlowEnabled() && !addToCartLoading) {
      giftWrapEventListener.clearPendingData();
    }
  }, [setGiftWrapModalOpen, resetGiftWrapState, userInput, resetPageContext, addToCartLoading]);

  // With the packaging journey the dialog waits until a box is known to be eligible.
  const isOpen = giftWrapModalOpen && (!packaging || packaging.ready);
  return <GiftWrapModal isOpen={isOpen} onClose={handleCloseGiftWrapModal} />;
}

/**
 * Main App component with providers.
 * @returns {(import('react').ReactElement | null)}
 */
function App() {
  const modals = <GiftWrapModals />;
  return (
    <UserInputProvider>
      <PageProvider>{isPackagingFlowEnabled() ? <PackagingProvider>{modals}</PackagingProvider> : modals}</PageProvider>
    </UserInputProvider>
  );
}

export default App;
